import { FormattedMessage, useIntl } from 'react-intl'
import { useBlueprintColumns } from '@hooks/useQuery'
import { JOIN_TYPES } from 'src/constants/queries'
import type { JoinNode, JoinType } from 'src/types/queries'

import './JoinEditor.css'

interface JoinEditorProps {
  node: JoinNode
  onChange: (node: JoinNode) => void
}

interface SelectOption {
  value: string
  disabled: boolean
}

interface ColumnSelectCellProps {
  value: string
  options: SelectOption[]
  placeholder: string
  disabled?: boolean
  onChange: (value: string) => void
}

function ColumnSelectCell({
  value,
  options,
  placeholder,
  disabled,
  onChange
}: ColumnSelectCellProps) {
  return (
    <select
      className="col-select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option disabled hidden value="">
        {placeholder}
      </option>
      {options.map((opt, idx) => (
        <option
          key={`${opt.value}-${idx}`}
          value={opt.value}
          disabled={opt.disabled}
        >
          {opt.value}
        </option>
      ))}
    </select>
  )
}

export default function JoinEditor({ node, onChange }: JoinEditorProps) {
  const intl = useIntl()

  const { colSpec: leftColSpec = [] } = useBlueprintColumns(
    node.left,
    `cols/${node.left.nodeId}`
  )
  const { colSpec: rightColSpec = [] } = useBlueprintColumns(
    node.right,
    `cols/${node.right.nodeId}`
  )

  const rightTypes = new Set(rightColSpec.map((c) => c.uiType))

  const handleLeftColumnChange = (index: number, value: string) => {
    const newLeftType = leftColSpec.find((c) => c.name === value)?.uiType
    const newOn = node.on.map((cond, i) => {
      if (i !== index) return cond
      const rightType = rightColSpec.find((c) => c.name === cond.right)?.uiType
      const keepRight = cond.right !== '' && rightType === newLeftType
      return { ...cond, left: value, right: keepRight ? cond.right : '' }
    })
    onChange({ ...node, on: newOn })
  }

  const handleRightColumnChange = (index: number, value: string) => {
    const newOn = node.on.map((cond, i) =>
      i === index ? { ...cond, right: value } : cond
    )
    onChange({ ...node, on: newOn })
  }

  const handleJoinTypeChange = (type: JoinType) => {
    onChange({ ...node, joinType: type })
  }

  const handleAddCondition = () => {
    onChange({ ...node, on: [...node.on, { left: '', right: '' }] })
  }

  const handleRemoveCondition = (index: number) => {
    const newOn = node.on.filter((_, i) => i !== index)
    onChange({ ...node, on: newOn })
  }

  const canRemove = node.on.length > 1

  return (
    <div id="join-editor">
      <header>
        <h3>
          <FormattedMessage id={`operation-editor.option.${node.type}`} />
        </h3>
      </header>
      <div className="type-selector">
        <label>
          <FormattedMessage id="join-editor.type.label" />
          {': '}
        </label>
        <select
          value={node.joinType}
          onChange={(e) => handleJoinTypeChange(e.target.value as JoinType)}
        >
          {JOIN_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div className="columns-title">
        <h4>
          <FormattedMessage id="join-editor.left-source.label" />
        </h4>
        <h4>
          <FormattedMessage id="join-editor.right-source.label" />
        </h4>
      </div>

      <ul className="columns-list">
        {node.on.map((condition, index) => {
          const otherLeftSelected = node.on
            .filter((_, i) => i !== index)
            .map((c) => c.left)
          const otherRightSelected = node.on
            .filter((_, i) => i !== index)
            .map((c) => c.right)
          const leftOptions: SelectOption[] = leftColSpec.map((c) => ({
            value: c.name,
            disabled:
              !rightTypes.has(c.uiType) || otherLeftSelected.includes(c.name)
          }))

          const leftType = leftColSpec.find(
            (c) => c.name === condition.left
          )?.uiType
          const rightOptions: SelectOption[] = rightColSpec.map((c) => ({
            value: c.name,
            disabled:
              !leftType ||
              c.uiType !== leftType ||
              otherRightSelected.includes(c.name)
          }))

          return (
            <li key={index} className="column">
              <ColumnSelectCell
                value={condition.left}
                options={leftOptions}
                placeholder={intl.formatMessage({
                  id: 'select-editor.column.placeholder'
                })}
                onChange={(val) => handleLeftColumnChange(index, val)}
              />
              <ColumnSelectCell
                value={condition.right}
                options={rightOptions}
                disabled={!condition.left}
                placeholder={intl.formatMessage({
                  id: 'select-editor.column.placeholder'
                })}
                onChange={(val) => handleRightColumnChange(index, val)}
              />
              <button
                type="button"
                className="remove-column"
                onClick={() => handleRemoveCondition(index)}
                disabled={!canRemove}
              >
                <span className="icon icon-bin" />
              </button>
            </li>
          )
        })}
      </ul>

      <button type="button" className="add-column" onClick={handleAddCondition}>
        <span className="icon icon-plus" />
      </button>
    </div>
  )
}
